/**
 * Exclusive accordion behavior for .js-product-accordion-item elements
 * (product accordions, FAQ accordions). Extracted from the legacy webpack
 * bundle (assets/scripts.js) so bundle-free pages — the MediSpa landing
 * pages — can use it standalone.
 *
 * All items on the page form one exclusive group: opening an item closes
 * the others; clicking an open item closes it. Each item exposes its
 * content height as the --content-height CSS variable, which the accordion
 * CSS uses for the expand/collapse transition.
 *
 * External code can drive an accordion programmatically by dispatching an
 * `accordion:toggle` CustomEvent on `document`:
 *
 *   document.dispatchEvent(new CustomEvent('accordion:toggle', {
 *     detail: {
 *       title: 'details',                  // handleized data-accordion-title value
 *       action: 'open' | 'close' | 'toggle' | 'close-all' // optional, defaults to 'toggle'
 *     }
 *   }));
 *
 * The exclusive-group behavior is preserved: opening via the event closes
 * every other item, matching the click handler. Use `close-all` (title not
 * required) to collapse every accordion on the page.
 */
type AccordionToggleDetail = {
  title?: string;
  action?: 'open' | 'close' | 'toggle' | 'close-all';
};

function initProductAccordions(): void {
  const items = Array.from(document.querySelectorAll<HTMLElement>('.js-product-accordion-item'));
  if (items.length === 0) return;

  const setContentHeight = (item: HTMLElement): void => {
    const content = item.querySelector<HTMLElement>('.js-product-accordion-content');
    if (content) {
      item.style.setProperty('--content-height', `${content.scrollHeight}px`);
    }
  };

  const closeItem = (item: HTMLElement): void => {
    item.classList.remove('is-active');
  };

  const openItem = (item: HTMLElement): void => {
    items.forEach((other) => other.classList.remove('is-active'));
    item.classList.add('is-active');
  };

  const toggleItem = (item: HTMLElement): void => {
    if (item.classList.contains('is-active')) {
      closeItem(item);
    } else {
      openItem(item);
    }
  };

  items.forEach((item) => {
    const trigger = item.querySelector<HTMLElement>('.js-accordion-trigger');

    // Skip click binding when the legacy assets/scripts.js bundle already
    // bound the trigger via trigger.onclick — classic deferred scripts
    // execute before module scripts, so onclick is set by the time this
    // runs on standard PDPs. On bundle-free Medispa LP pages, onclick is
    // null so this module owns the click behavior.
    if (trigger && !trigger.onclick) {
      trigger.addEventListener('click', () => {
        toggleItem(item);
      });
    }

    setContentHeight(item);
  });

  // Allow external code to open/close an accordion by its handleized
  // data-accordion-title value. The exclusive-group behavior is preserved:
  // opening via the event closes every other item.
  document.addEventListener('accordion:toggle', (event) => {
    const { title, action = 'toggle' } =
      (event as CustomEvent<AccordionToggleDetail>).detail ?? {};

    switch (action) {
      case 'close-all':
        items.forEach(closeItem);
        return;
      case 'open':
      case 'close':
      case 'toggle':
        break;
      default:
        return;
    }

    if (!title) return;

    const target = document.querySelector<HTMLElement>(
      `.js-product-accordion-item[data-accordion-title="${CSS.escape(title)}"]`,
    );
    if (!target) return;

    switch (action) {
      case 'open':
        if (!target.classList.contains('is-active')) openItem(target);
        break;
      case 'close':
        closeItem(target);
        break;
      default:
        toggleItem(target);
    }
  });

  // The legacy bundle measured once at init; recompute on resize so
  // reflowed rich-text answers don't get clipped.
  window.addEventListener('resize', () => {
    items.forEach(setContentHeight);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductAccordions);
} else {
  initProductAccordions();
}
